import React, { createContext, useContext, useEffect, useState, useRef, type ReactNode } from "react";
import { Moon, Sun } from "lucide-react";
import { animateThemeToggle } from "./motion.ts";

export type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const STORAGE_KEY = "app_theme";

export function getSystemTheme(): Theme {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
}

function getInitialTheme(): Theme {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
    return getSystemTheme();
  }
  return "light";
}

const ThemeContext = createContext<ThemeContextType>({
  theme: "light",
  setTheme: () => {},
  toggleTheme: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  const setTheme = (nextTheme: Theme) => {
    if (typeof document !== "undefined") {
      const prefersReduced =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

      const applyTheme = () => {
        setThemeState(nextTheme);
        localStorage.setItem(STORAGE_KEY, nextTheme);
        document.documentElement.setAttribute("data-theme", nextTheme);
        document.documentElement.classList.remove("theme-light", "theme-dark");
        document.documentElement.classList.add(`theme-${nextTheme}`);
      };

      if (!prefersReduced && "startViewTransition" in document) {
        (document as any).startViewTransition(() => {
          applyTheme();
        });
      } else {
        document.documentElement.classList.add("theme-transitioning");
        applyTheme();
        setTimeout(() => {
          document.documentElement.classList.remove("theme-transitioning");
        }, 350);
      }
    } else {
      setThemeState(nextTheme);
    }
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  // Automatically sync with OS color scheme changes if user hasn't explicitly locked a preference
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleOsThemeChange = (event: MediaQueryListEvent) => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        const newSystemTheme: Theme = event.matches ? "dark" : "light";
        setThemeState(newSystemTheme);
        document.documentElement.setAttribute("data-theme", newSystemTheme);
        document.documentElement.classList.remove("theme-light", "theme-dark");
        document.documentElement.classList.add(`theme-${newSystemTheme}`);
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleOsThemeChange);
      return () => mediaQuery.removeEventListener("change", handleOsThemeChange);
    } else if ((mediaQuery as any).addListener) {
      (mediaQuery as any).addListener(handleOsThemeChange);
      return () => (mediaQuery as any).removeListener(handleOsThemeChange);
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-theme", theme);
      document.documentElement.classList.remove("theme-light", "theme-dark");
      document.documentElement.classList.add(`theme-${theme}`);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeToggleBtn({ className = "" }: { className?: string }) {
  const { theme, toggleTheme } = useTheme();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (iconRef.current) {
      animateThemeToggle(buttonRef.current, iconRef.current, theme === "dark");
    }
  }, [theme]);

  const isLight = theme === "light";

  return (
    <button
      ref={buttonRef}
      type="button"
      className={`icon-button theme-toggle-btn ${className}`}
      onClick={toggleTheme}
      title={isLight ? "切换至深空黑曜控制台 (Deep Obsidian Dark)" : "切换至温润白瓷浅色 (Warm Alabaster Light)"}
      aria-label={isLight ? "切换至暗色模式" : "切换至浅色模式"}
    >
      <span ref={iconRef} className="theme-toggle-icon" style={{ display: "inline-flex" }}>
        {isLight ? <Moon size={16} /> : <Sun size={16} />}
      </span>
    </button>
  );
}
